import React, { Fragment, SyntheticEvent } from 'react';
import { useDeleteWithConfirmController, useTranslate } from 'react-admin';
import {
  MutationMode,
  OnFailure,
  OnSuccess,
  Record as RaRecord,
  RedirectionSideEffect,
} from 'ra-core';

import DeleteButton from '~/components/DeleteButton';
import { ConfirmationBase } from '~/components/Confirmation';
import defaultOnError from '~/errorsHandler';

export interface DeleteWithConfirmButtonProps {
  record?: RaRecord;
  resource: string;
  basePath?: string;
  redirect?: RedirectionSideEffect;
  mutationMode?: MutationMode;
  label?: string;
  disabled?: boolean;
  confirmHeading?: string;
  confirmContent?: string;
  onSuccess?: OnSuccess;
  onFailure?: OnFailure;
}

const noopEvent = {
  stopPropagation: () => {},
} as SyntheticEvent;

const DeleteWithConfirmButton = (props: DeleteWithConfirmButtonProps) => {
  const {
    record,
    resource,
    basePath,
    redirect = 'list',
    mutationMode = 'pessimistic',
    label,
    disabled,
    confirmHeading,
    confirmContent,
    onSuccess,
    onFailure = defaultOnError,
  } = props;

  const translate = useTranslate();
  const t = (key: string): string => translate(`utils.confirmation.${key}`);

  const {
    open,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    record,
    resource,
    basePath,
    redirect,
    mutationMode,
    onSuccess,
    onFailure,
  });

  return (
    <Fragment>
      <DeleteButton
        onClick={handleDialogOpen}
        loading={loading}
        disabled={disabled}
        {...(label ? { label } : {})}
      />
      <ConfirmationBase
        open={open}
        handleClose={() => handleDialogClose(noopEvent)}
        onConfirm={() => handleDelete(noopEvent)}
        confirmationSettings={{
          variant: 'modal',
          modal: {
            heading: confirmHeading ?? t('warning'),
            content: confirmContent ?? t('content'),
          },
        }}
      />
    </Fragment>
  );
};

export default DeleteWithConfirmButton;
